<template>
    <div class="title">
        <h2 class="text_title">اگر قبلا سرمایه‌گذاری می‌کردی…</h2>
        <p>به کمک این ابزار می‌تونید نمایی از گذشته تا به امروز بازارهای مختلف سرمایه‌گذاری رو مشاهده کنید و با آگاهی کامل از میزان سود و ضرر بازارها در بازه‌های زمانی مختلف، سرمایه‌گذاری‌های سنجیده‌تر‌ و هدفمند‌تری داشته باشید!
        </p>
    </div>
    <div class="box_pnl">
        <div>
            <div class="chart_header">
                <div class="boxes">
                    <div class="lowestEfficiency">
                        <p class="success">بیشترین بازدهی</p>
                        <span> {{ maxPnl.name }}</span>
                        <span dir="ltr">{{
                                maxPnl?.percentage?.toLocaleString("en-US", {
                                    signDisplay: "always",
                                    maximumFractionDigits: 2
                                })
                            }}%</span>
                    </div>
                    <div class="highestEfficiency">
                        <p class="danger">کم ترین بازدهی</p>
                        <span> {{ minPnl.name }}</span>
                        <span dir="ltr">{{
                                minPnl?.percentage?.toLocaleString("en-US", {
                                    signDisplay: "always",
                                    maximumFractionDigits: 2
                                })
                            }}%</span>
                    </div>
                </div>
            </div>
            <div class="range__slider">
                <div class="slider_title">
                    <span>بازه زمانی </span>
                    <span>از {{ nodeLeft }} تا {{ nodeRight }}</span>
                </div>
                <range-slider
                    width="95%" :node-size="13" :node-count="Object.keys(seasons).length - 1" :show-nodes="false"
                    @left-range="getLeftNodeRange"
                    @range-finalizer="getPNLData()"
                    @right-range="getRightNodeRange"
                />
            </div>
            <div class="range__slider">
                <div class="slider_title">
                    <span>میزان سرمایه گذاری</span>
                    <span> {{ MIN_DEPOSIT.toLocaleString("en-US") }} تومان</span>
                </div>
                <Seekbar
                    :default-percentage="0"
                    :show-nodes="false"
                    :show-percentage="false"
                    :node-size="8"
                    :node-count="20"
                    :attraction-unit="5"
                    line-color="var(--identity)"
                    :node-color="theme == 'dark' ? `var(--gray-300)` : `var(--gray-200)`"
                    width="95%"
                    @range-finalizer="getPNLData()"
                    @selected="calculateSellAmount"
                />
            </div>
        </div>
        <div class="chart">
            <HorizontalChart
                :labels="pnlData.map(item=>item.name)"
                :prices="pnlData.map(item=>item.percentage)"
                :amounts="pnlData.map(item=>item.amount)"
            />
        </div>
    </div>
</template>
<script setup lang="ts">
import {onMounted, ref} from "vue";
import RangeSlider from "./components/range-slider.vue";
import Seekbar from "./components/seekbar.vue";
import HorizontalChart from "./components/horizontal-chart.vue";

type PNL_DATA = { name: string, amount: number, percentage: number };

const seasons = ref<Record<string, string>>({})
const pnlData = ref<PNL_DATA[]>([])
const theme = ref<string | null>(localStorage.getItem('theme') ? localStorage.getItem('theme') : null)
const depositAmount = ref(1_000_000)
const fromDate = ref<string>('')
const toDate = ref<string>('')

const MIN_DEPOSIT = ref<number>(1_000_000)
const MAX_DEPOSIT = 100_000_000
const defaultPercentageAmount = ref<number>(0)

const baseUrl = 'https://client.saraf.app'

const minPnl = ref<PNL_DATA>({
    amount: 0,
    name: "",
    percentage: 0
})
const maxPnl = ref<PNL_DATA>({
    amount: 0,
    name: "",
    percentage: 0
})

const nodeLeft = ref<string | null>('')
const nodeRight = ref<string | null>('')

function getRightNodeRange(percent: number) {
    let indexSelected = (percent / 100) * Object.keys(seasons.value).length
    if (Math.floor(indexSelected) === 0) {
        indexSelected = 1
    }
    if (indexSelected >= Object.keys(seasons.value).length) {
        nodeRight.value = Object.keys(seasons.value)[Object.keys(seasons.value).length - 1]
    } else {
        nodeRight.value = Object.keys(seasons.value)[Math.floor(indexSelected)]
    }
    toDate.value = seasons.value[nodeRight.value]
}

function getLeftNodeRange(percent: number) {
    const indexSelected = (percent / 100) * (Object.keys(seasons.value).length - 1)
    nodeLeft.value = Object.keys(seasons.value)[Math.ceil(indexSelected)]
    fromDate.value = seasons.value[nodeLeft.value]
}

function calculateSellAmount(value: number) {
    if (value === 0) value = value + 1;
    MIN_DEPOSIT.value = Math.floor(+(value / 100) * MAX_DEPOSIT);
    depositAmount.value = MIN_DEPOSIT.value
    defaultPercentageAmount.value = 5
}

async function getSeasons() {
    await fetch(baseUrl + '/v1/pnl/seasons', {
        method: 'get'
    }).then((value) => {
        JSON.stringify(value)
        return value.json() as Promise<{
            result: boolean,
            seasons: Record<string, string>
        }>
    }).then((response) => {
        if (response.result) {
            seasons.value = response.seasons;
            getLeftNodeRange(0)
            getRightNodeRange(100)
        }
    })
}

async function getPNLData() {
    await fetch(baseUrl + '/v1/pnl/history',
        {
            method: 'post',
            headers: {'Content-Type': 'application/json;charset=UTF-8'},
            body: JSON.stringify({
                deposit: depositAmount.value,
                fromDate: fromDate.value,
                toDate: toDate.value
            })
        }).then((value) => {
        JSON.stringify(value)
        return value.json() as Promise<{
            result: boolean,
            list: Record<string, number>
        }>
    }).then((response) => {
        if (response.result) {
            pnlData.value = []
            for (const item in response.list) {
                pnlData.value.push({
                    name: item,
                    percentage: ((response.list[item] - depositAmount.value) / depositAmount.value) * 100,
                    amount: response.list[item]
                });
            }

            pnlData.value.sort((a, b) => {
                if (a.percentage > b.percentage)
                    return -1;

                if (a.percentage < b.percentage)
                    return 1;

                return 0;
            });

            getMinMax()
        }
    })
}

function getMinMax() {
    let max = 1
    let min = 99999999999

    const minData = {}
    const maxData = {}

    for (const item of pnlData.value) {
        if (item.percentage >= max) {
            max = item.percentage
            Object.assign(maxData, {...item})
        }

        if (item.percentage <= min) {
            min = item.percentage
            Object.assign(minData, {...item})
        }
    }

    minPnl.value = minData as PNL_DATA;
    maxPnl.value = maxData as PNL_DATA;
}

onMounted(async () => {
    await getSeasons()
    await getPNLData()
})
</script>
<style lang="scss">
.title {
    text-align: center;
    margin: 16px 0;

    p {
        font-size: 16px;
        line-height: 24px;
        margin-top: 16px;
    }
}

.box_pnl {
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 2fr;
    gap: 12px;
    align-items: center;
    & > div:first-child {
      background-color: var(--gray-0);
      border-radius: 20px;
      padding: 36px;
    }
}

.chart_header {
    border-bottom: 1px solid var(--gray-200);

    .boxes {
        display: flex;
        padding: 20px 0;

        .lowestEfficiency, .highestEfficiency {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            width: 50%;
            text-align: center;

            p {
                font-size: 14px;
                font-weight: 700;
            }

            span {
                margin-block: 4px;
                font-weight: 700;
                color: white;

                &:first-child {
                    font-size: 16px;
                }

                &:last-child {
                    font-size: 14px;
                    color: var(--gray-400);
                }
            }

        }

        .success {
            color: var(--success);
        }

        .danger {
            color: var(--danger);
        }
    }
}

.range__slider {
    padding: 20px 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .slider_title {
        font-size: 13px;
        font-weight: 600;
        margin-bottom: 10px;
        padding: 5px 0;
        width: 100%;
        display: flex;
        justify-content: space-between;
    }

    .slider_sub_title {
        font-size: 12px !important;
        color: var(--gray-200);
    }
}

@media screen and (max-width: 768px) {
    .box_pnl {
        width: 100%;
        display: grid;
        grid-template-columns: 1fr;
        gap: 12px;
        align-items: center;
    }
}


[data-theme='dark'] {
    .title {
        h2 {
            color: var(--white);
        }

        p {
            color: var(--gray-500);
        }
    }

    .slider_sub_title {
        font-size: 12px !important;
        color: var(--gray-500);
    }

    .lowestEfficiency,.highestEfficiency span{
        color: var(--gray-500);
    }
    .slider_title span{
        color: var(--gray-500);
    }
}
</style>
